import { receiveQuery } from "../../../containers/CohortBuilder/cohortBuilderSlice"
import { findQuery, randomInt, toURL } from "../../../reducers/util"
import { AppState } from "../../../types/AppState"
import { Query } from "../../HealthcareUsageReport/types/CohortQuery"
import { findPathway } from "../reducers"
import { CohortPathway, DestinationFilter, EventConfig, EventFilter } from "../types/CohortPathway"
import { DestinationMetric } from "../types/EventMetric"
import {CONFIG} from "../../../util/config";

export const newPathway = (queryId: number) => ({
    type: 'PATHWAY_NEW_PATHWAY',
    queryId
})

export const generatePathway = (query: Query) => {
    return (dispatch: any, getState: () => AppState) => {
        const url = toURL(`/pathway/generate?queryId=${query.id}`)
        fetch(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            ...(CONFIG.isDev && { credentials: 'include' })
        })
            .then(response => response.json())
            .then(json => {
                dispatch(receiveQuery({ queryId: query.id, query: json }))
            })
    }
}

export const addPathway = (pathway: CohortPathway) => ({
    type: 'PATHWAY_ADD_PATHWAY',
    pathway
})

export const setCurrentPathway = (pathwayId: number) => ({
    type: 'PATHWAY_SET_CURRENT_PATHWAY',
    pathwayId
})

export const setDefaultPathway = (query: Query, exceptPathwayId: number | undefined = undefined) => ({
    type: 'PATHWAY_SET_DEFAULT_PATHWAY',
    query,
    exceptPathwayId
})

export const setNumberOfEvents = (noOfEvents: number) => ({
    type: 'PATHWAY_SET_NUMBER_OF_EVENTS',
    noOfEvents
})

export const incrementEvents = () => ({
    type: 'PATHWAY_INCREMENT_EVENTS'
})

export const decrementEvents = () => ({
    type: 'PATHWAY_DECREMENT_EVENTS'
})

export const updatePathway = (pathway: CohortPathway) => ({
    type: 'PATHWAY_UPDATE_PATHWAY',
    pathway
})

export function loadPathwayMetrics() {
    return (dispatch: any, getState: () => AppState) => {
        const pathway = findPathway(getState().pathway.pathways, getState().pathway.pathwayId!)
        const url = toURL(`/pathway/metrics?queryId=${getState().current.queryId}`)
        return fetch(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(pathway, null, 2),
            ...(CONFIG.isDev && { credentials: 'include' })
        })
            .then(response => response.json())
            .then(json => dispatch(updatePathway(json)))
    }
}

export const setCurrentPathwayTitle = (title: string) => ({
    type: 'PATHWAY_SET_CURRENT_PATHWAY_TITLE',
    title
})

export function savePathway(pathwayId: number, queryId: number) {
    return (dispatch: any, getState: () => AppState) => {
        const pathway = findPathway(getState().pathway.pathways, pathwayId)!
        _savePathway(pathway, queryId)
            .then(pathway => {
                if (pathwayId < 0) {
                    // pathway has not been saved before
                    dispatch(addPathway(pathway))
                    dispatch(deletePathwayFromState(pathwayId))
                }
                dispatch(setCurrentPathway(pathway.id))
            })
    }
}

function _savePathway(pathway: CohortPathway, queryId: number) {
    const url = toURL('/pathway/pathway?queryId=' + queryId)
    return fetch(url, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(pathway, null, 2),
        ...(CONFIG.isDev && { credentials: 'include' })
    })
        .then(response => response.json())
}

export function setCurrentPathwayAndRefresh() {
    return (dispatch: any, getState: () => AppState) => {
        dispatch(refreshSavedPathwaysForCurrentQuery())
    }
}

function refreshSavedPathwaysForCurrentQuery() {
    return (dispatch: any, getState: () => AppState) => {
        const queryId = getState().current.queryId!
        const url = toURL('/pathway/pathways?queryId=' + queryId)
        return fetch(url, {
            ...(CONFIG.isDev && { credentials: 'include' })
        })
            .then(response => response.json())
            .then(pathways => {
                dispatch(setPathwaysForCurrentQuery(pathways))
                const state = getState().current
                const query = findQuery(state.study, state.queryId!)
                dispatch(setDefaultPathway(query!))
            })
            .catch((error) => console.log('some error happened while fetching the pathways' + error))
    }
}

export const setPathwaysForCurrentQuery = (pathways: CohortPathway[]) => ({
    type: 'PATHWAY_SET_PATHWAYS_FOR_CURRENT_QUERY',
    pathways
})

export const filterClose = () => ({
    type: 'PATHWAY_FILTER_CLOSE'
})

export const filterOnDestinationMetric = (destinationMetric: DestinationMetric) => ({
    type: 'PATHWAY_FILTER_ON_DESTINATION_METRIC',
    destinationMetric
})

export const upateFilterOnDestinationMetric = (destinationFilter: DestinationFilter) => ({
    type: 'PATHWAY_UPDATE_FILTER_ON_DESTINATION_METRIC',
    destinationFilter
})

export const filterOnEvent = (eventConfig: EventConfig) => ({
    type: 'PATHWAY_FILTER_ON_EVENT',
    eventConfig
})

export const upateFilterOnEvent = (eventFilter: EventFilter) => ({
    type: 'PATHWAY_UPDATE_FILTER_ON_EVENT',
    eventFilter
})

export const toggleDestinationMetric = (destinationMetric: DestinationMetric) => ({
    type: 'PATHWAY_TOGGLE_DESTINATION_METRIC',
    destinationMetric
})

export function toggleDestinationMetricAndUpdate(destinationMetric: DestinationMetric) {
    return (dispatch: any, getState: () => AppState) => {
        dispatch(toggleDestinationMetric(destinationMetric))
        dispatch(loadPathwayMetrics())
    }
}

export const copyPathway = (pathwayId: number) => {
    return (dispatch: any, getState: () => AppState) => {
        const pathway = findPathway(getState().pathway.pathways, pathwayId)!
        const copiedPathway: CohortPathway = JSON.parse(JSON.stringify(pathway))
        copiedPathway.id = randomInt()
        copiedPathway.createdOn = new Date().getTime()
        copiedPathway.title = 'Copy of ' + pathway.title
        dispatch(addPathway(copiedPathway))
    }
}

export const deletePathwayFromState = (pathwayId: number) => ({
    type: 'PATHWAY_DELETE_PATHWAY_FROM_STATE',
    pathwayId
})

export const deletePathway = (pathwayId: number) => {
    return (dispatch: any, getState: () => AppState) => {
        const action = () => {
            if (pathwayId === getState().pathway.pathwayId) {
                const state = getState().current
                const query = findQuery(state.study, state.queryId!)
                dispatch(setDefaultPathway(query!, pathwayId))
            }
            dispatch(deletePathwayFromState(pathwayId))
        }
        if (pathwayId < 0) {
            action()
        } else {
            const url = toURL(`/pathway/pathway?pathwayId=${pathwayId}`)
            return fetch(url, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                ...(CONFIG.isDev && { credentials: 'include' })
            }).then(response => {
                action()
            })
        }
    }
}